<template>
  <div class="two-factor-card">
    <div class="card-header-inner">
      <div class="header-content">
        <div class="icon-wrapper" :class="{ 'is-enabled': isEnabled }">
          <i class="fas fa-shield-alt"></i>
        </div>
        <div class="text-content">
          <h5 class="title">Two-Factor Authentication</h5>
          <p class="description" v-if="isEnabled">
            Your account is currently secured with an extra layer of protection.
          </p>
          <p class="description" v-else>
            Add an extra layer of security to your account.
          </p>
        </div>
      </div>
      
      <div class="toggle-wrapper">
        <label class="switch">
          <input type="checkbox" :checked="isEnabled" @click.prevent="handleToggleClick">
          <span class="slider round"></span>
        </label>
      </div>
    </div>

    <div v-if="isEnabled" class="card-footer-inner">
      <button class="btn-recovery" @click="showRecovery = true">
        <i class="fas fa-sync-alt me-2"></i> Regenerate Recovery Codes
      </button>

      <!-- Recovery Codes Warning -->
      <div class="recovery-warning-box mt-3">
        <div class="warning-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="warning-text">
          <p><strong>Warning:</strong> This will invalidate all {{ unusedCount }} existing codes. You must download the new codes immediately.</p>
        </div>
      </div>
    </div>

    <!-- Setup Modal -->
    <two-factor-setup v-if="showSetup" @close="onSetupClose" />
    
    <!-- Recovery Codes Modal -->
    <recovery-codes v-if="showRecovery" @close="showRecovery = false" />

    <!-- Disable Confirmation Modal -->
    <div v-if="showDisableConfirm" class="modal-overlay" @click.self="showDisableConfirm = false">
      <div class="modal-content custom-modal">
        <div class="modal-header-clean">
          <h4>Disable 2FA</h4>
          <button class="close-btn" @click="showDisableConfirm = false">&times;</button>
        </div>
        
        <div class="modal-body-clean">
          <div class="alert-warning-clean mb-4">
            <i class="fas fa-exclamation-triangle"></i>
            <p>Disabling 2FA will remove the extra security from your account. You will need to re-configure it to enable it again.</p>
          </div>

          <div class="form-group-clean">
            <label>Confirm Your Password</label>
            <input 
              v-model="password" 
              type="password" 
              class="input-clean" 
              placeholder="Enter current password"
              @keyup.enter="handleDisable"
            />
          </div>

          <div v-if="error" class="error-text mt-2">{{ error }}</div>
        </div>

        <div class="modal-actions-clean">
          <button class="btn-cancel" @click="showDisableConfirm = false">Cancel</button>
          <button class="btn-confirm-danger" @click="handleDisable" :disabled="!password || loading">
            <span v-if="loading"><i class="fas fa-spinner fa-spin"></i></span>
            <span v-else>Disable 2FA</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import TwoFactorSetup from './TwoFactorSetup.vue';
import RecoveryCodes from './RecoveryCodes.vue';

/**
 * Component to toggle Two-Factor Authentication (2FA) in account settings.
 * Provides a modern switch UI and handles enablement/disablement flows.
 */
export default {
    name: 'TwoFactorToggle',
    components: {
        TwoFactorSetup,
        RecoveryCodes
    },
    data() {
        return {
            showSetup: false,
            showRecovery: false,
            showDisableConfirm: false,
            password: '',
            loading: false,
            error: '',
            unusedCount: 0
        };
    },
    mounted() {
        if (this.isEnabled) {
            this.fetchUnusedCount();
        }
    },
    computed: {
        ...mapGetters('Auth', ['getUser']),
        
        /**
         * Check if 2FA is currently enabled for the user.
         * @returns {boolean}
         */
        isEnabled() {
            return !!this.getUser?.two_factor_confirmed_at;
        }
    },
    watch: {
        isEnabled(val) {
            if (val) {
                this.fetchUnusedCount();
            }
        },
        showRecovery(val) {
            if (!val) {
                // Refresh count when recovery modal closes (in case they regenerated)
                this.fetchUnusedCount();
            }
        }
    },
    methods: {
        /**
         * Handle switch click action.
         * Opens setup modal if enabling, or confirmation modal if disabling.
         */
        handleToggleClick() {
            if (!this.isEnabled) {
                this.showSetup = true;
            } else {
                this.showDisableConfirm = true;
            }
        },

        /**
         * Closes the setup modal and resets state.
         */
        onSetupClose() {
            this.showSetup = false;
        },
        
        /**
         * Confirm and execute 2FA disablement.
         * Requires user password verification.
         */
        async handleDisable() {
            if (!this.password) return;
            
            this.loading = true;
            this.error = '';
            
            try {
                await this.$store.dispatch('TwoFactor/disable', this.password);
                this.showDisableConfirm = false;
                this.password = '';
            } catch (err) {
                this.error = err.response?.data?.message || 'Failed to disable 2FA. Check your password.';
            } finally {
                this.loading = false;
            }
        },

        /**
         * Fetch the number of unused recovery codes.
         */
        async fetchUnusedCount() {
            try {
                const res = await this.$store.dispatch('TwoFactor/getRecoveryCodes');
                const data = res.data || res.message || res;
                if (data.unused_codes_count !== undefined) {
                    this.unusedCount = data.unused_codes_count;
                }
            } catch (err) {
                console.error('[TwoFactorToggle] Failed to fetch unused count:', err);
            }
        }
    }
}
</script>

<style scoped>
.two-factor-card {
    background: #ffffff;
    border: 1px solid #f1f1f1;
    border-radius: 12px;
    padding: 24px;
    margin-top: 32px;
}

.card-header-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-content {
    display: flex;
    gap: 16px;
    align-items: center;
}

.icon-wrapper {
    width: 48px;
    height: 48px;
    background: #f3f4f6;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    font-size: 20px;
}

.icon-wrapper.is-enabled {
    background: var(--secondary-color, rgba(252, 102, 102, 0.1));
    color: var(--primary-color, #fc6666);
}

.text-content .title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
    color: #111827;
}

.text-content .description {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
}

.card-footer-inner {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #f3f4f6;
}

.btn-recovery {
    background: transparent;
    border: 1px solid #e5e7eb;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-recovery:hover {
    background: #f9fafb;
    border-color: #d1d5db;
}

/* Warning Box Styles */
.recovery-warning-box {
    display: flex;
    gap: 16px;
    background: #fff5f5;
    border: 1px solid #feb2b2;
    border-radius: 12px;
    padding: 16px;
    align-items: flex-start;
}

.warning-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    background: #fee2e2;
    color: #e53e3e;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
}

.warning-text p {
    margin: 0;
    font-size: 13px;
    color: #c53030;
    line-height: 1.5;
}

.warning-text strong {
    font-weight: 700;
}

/* Toggle Switch Styling */
.switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #e5e7eb;
    transition: .4s;
}

.slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .4s;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

input:checked + .slider {
    background-color: var(--primary-color, #fc6666);
}

input:focus + .slider {
    box-shadow: 0 0 1px var(--primary-color, #fc6666);
}

input:checked + .slider:before {
    transform: translateX(20px);
}

.slider.round {
    border-radius: 24px;
}

.slider.round:before {
    border-radius: 50%;
}

/* Modal Styling Inspired by Connection Detail */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
    backdrop-filter: blur(2px);
}

.custom-modal {
    background: white;
    width: 90%;
    max-width: 450px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header-clean {
    padding: 20px 24px;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header-clean h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #9ca3af;
    cursor: pointer;
}

.modal-body-clean {
    padding: 24px;
}

.alert-warning-clean {
    background: #fffbeb;
    border: 1px solid #fde68a;
    padding: 16px;
    border-radius: 8px;
    display: flex;
    gap: 12px;
}

.alert-warning-clean i {
    color: #d97706;
    margin-top: 3px;
}

.alert-warning-clean p {
    margin: 0;
    font-size: 13px;
    color: #92400e;
    line-height: 1.5;
}

.form-group-clean label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
}

.input-clean {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
}

.input-clean:focus {
    border-color: var(--primary-color, #fc6666);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(252, 102, 102, 0.1));
}

.modal-actions-clean {
    padding: 16px 24px;
    background: #f9fafb;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.btn-cancel {
    padding: 9px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
}

.btn-confirm-danger {
    padding: 9px 24px;
    background: var(--primary-color, #ef4444);
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
}

.btn-confirm-danger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.error-text {
    color: #ef4444;
    font-size: 12px;
}
</style>
